import { createContext, useContext, type ReactNode } from 'react';

/**
 * Per-node action handlers exposed to GenericNode.
 *
 * Why a context instead of threading through node.data:
 *
 *   1. ReactFlow re-renders nodes when ``data`` identity changes.
 *      Stuffing a callback into data would cause every node to
 *      re-render whenever the editor's callback identity changed
 *      (e.g. when ``nodes``/``edges`` deps shifted).
 *   2. Node action handlers are conceptually editor-scoped, not
 *      data-scoped -- the same handler applies to every node and
 *      doesn't belong on individual node payloads.
 *   3. Easy to extend with future per-node actions (open chat,
 *      copy result, etc.) without bloating ``NodeData``.
 *
 * Provider wraps the ReactFlow surface inside ``NodeCanvas``;
 * GenericNode reads via the typed hook below.
 */
export interface NodeActions {
  /**
   * Click-handler for the "Re-run from this node" play button on
   * each node card.  Editor resolves to either a paused-resume
   * (with this node's id forced into editedNodeIds) or a fresh
   * rerun-from-node start, depending on context.
   *
   * Optional: callers that don't wire this in (e.g. read-only
   * embeds) get an inert button -- GenericNode skips rendering
   * the play button entirely when this is absent.
   */
  onRerunFromHere?: (nodeId: string) => void;
}

const NodeActionsContext = createContext<NodeActions>({});

export interface NodeActionsProviderProps extends NodeActions {
  children: ReactNode;
}

export const NodeActionsProvider = ({
  children,
  ...actions
}: NodeActionsProviderProps) => {
  // Callers should already memoise the action callbacks; we don't
  // wrap in useMemo here because the spread already preserves
  // identity equality when the underlying callbacks are stable.
  return (
    <NodeActionsContext.Provider value={actions}>
      {children}
    </NodeActionsContext.Provider>
  );
};

/** Read the editor-scoped node-action callbacks.  Always safe to
 * call -- returns an empty object if no provider is mounted (e.g.
 * snapshot / preview surfaces that render nodes outside the
 * editor). */
export const useNodeActions = (): NodeActions => useContext(NodeActionsContext);
